import { fieldStateCss } from '@src/components/common/fieldStateStyles';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import {
  type CSSProperties,
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../../svgs/DeleteIcon';
import SimplePencilIcon from '../../../svgs/SimplePencilIcon';
import { SortChevronIcon } from '../../../svgs/SortChevronIcon';
import { handleLoadGeneratedContent } from '../../../utility/general';
import {
  ModalButtonsContainer,
  ModalCancelButton,
  ModalConfirmButton,
  ModalHeading,
  ModalWrapper,
} from '../../common/ConfirmModal';
import Modal from '../../common/Modal';
import { BlogProducer } from './BlogProducer';

import type { BlogOrEmailContent } from '../../../types.ts/general';

type ContentRow = {
  id: number;
  title: string;
  summary: string;
  theme: string;
  builder: string;
  words: number;
  lastModified: string;
  /**
   * Numeric timestamp derived from `BlogProducer.getModifiedAt`'s source
   * (falls back to the entry id, which is itself `Date.now()` when saved).
   * Used for sorting the Last Modified column; stringly-formatted
   * `lastModified` wouldn't compare correctly with localeCompare.
   */
  lastModifiedTs: number;
  raw: BlogOrEmailContent;
};

type SortKey = 'title' | 'theme' | 'builder' | 'words' | 'lastModified';

type SortDirection = 'asc' | 'desc';

const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const countWords = (html: string): number => {
  const plainText = stripHtml(html);
  if (!plainText) return 0;
  return plainText.split(/\s+/).filter(word => word.length > 0).length;
};

const extractSummary = (html: string, maxLength = 120): string => {
  const text = stripHtml(html);
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '...';
};

type Props = {
  type: 'blog' | 'socialPost' | 'email';
  entries: BlogOrEmailContent[];
  setCurrentContent: Dispatch<SetStateAction<BlogOrEmailContent | null>>;
  onSelectArticle?: (articleContent: string) => void;
  onDelete?: (contentId: number) => void;
  onRename?: (contentId: number, newDisplayTitle: string) => void;
  hideSectionTitle?: boolean;
};

const ArticlesTable = observer(
  ({
    type,
    entries,
    setCurrentContent,
    onSelectArticle,
    onDelete,
    onRename,
    hideSectionTitle,
  }: Props) => {
    const videoCreator = useVideoCreatorStore();
    const savedData =
      type === 'blog'
        ? videoCreator.story?.savedBlog
        : type === 'email'
          ? videoCreator.story?.savedEmail
          : videoCreator.story?.savedSocialPost;
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState('');
    const titleInputRef = useRef<HTMLInputElement>(null);
    const originalTitleRef = useRef('');

    // Sort state — default to Last Modified desc, which matches the previous
    // hard-coded `.sort((a, b) => b.id - a.id)` behavior so nothing visibly
    // changes for users who don't interact with the headers.
    const [sortKey, setSortKey] = useState<SortKey>('lastModified');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSort = useCallback((key: SortKey) => {
      setSortKey(prev => {
        if (prev === key) {
          setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
          return prev;
        }
        // First click on a new column: desc for numeric/date (feels natural —
        // biggest first), asc for text.
        setSortDirection(
          key === 'words' || key === 'lastModified' ? 'desc' : 'asc',
        );
        return key;
      });
    }, []);

    const handleStartEdit = useCallback(
      (e: MouseEvent<HTMLButtonElement>, row: ContentRow) => {
        e.stopPropagation();
        if (!onRename) return;
        setEditingRowId(row.id);
        setEditingValue(row.title);
        originalTitleRef.current = row.title;
        setTimeout(() => titleInputRef.current?.select(), 0);
      },
      [onRename],
    );

    const handleFinishEdit = useCallback(
      (rowId: number) => {
        setEditingRowId(null);
        const trimmed = editingValue.trim();
        if (!trimmed || trimmed === originalTitleRef.current) return;
        onRename?.(rowId, trimmed);
      },
      [editingValue, onRename],
    );

    const parseArticleTitle = (fullTitle: string): string => {
      if (type !== 'blog') return fullTitle;
      const parts = fullTitle.split(' - ');
      if (parts.length >= 3) {
        // Remove first (theme) and last (date) parts, rejoin middle
        return parts.slice(1, -1).join(' - ');
      }
      return fullTitle;
    };

    const rows: ContentRow[] = useMemo(() => {
      if (!savedData || entries.length === 0) return [];

      return entries
        .filter(entry => !!savedData[entry.id])
        .map(entry => {
          const data = savedData[entry.id];
          const versions = data?.version_history?.versions;

          // Find generation details from any version that has them
          const generationDetails = versions?.find(
            (v: any) => v.generationDetails,
          )?.generationDetails;

          // Extract theme from title format "Theme - Title - Date"
          const titleParts = entry.title.split(' - ');
          const themeFromTitle =
            titleParts.length >= 3 ? titleParts[0] : undefined;

          return {
            id: entry.id,
            title: parseArticleTitle(entry.title) || 'Untitled',
            summary: extractSummary(entry.content || ''),
            theme: themeFromTitle || generationDetails?.templateName || '—',
            builder: entry.username || '—',
            words: countWords(entry.content || ''),
            lastModified: BlogProducer.getModifiedAt(entry.id) || '—',
            /* entry.id is `Date.now()` at save time — a stable numeric proxy
               for last-modified that works without parsing the formatted
               string. */
            lastModifiedTs: entry.id,
            raw: entry,
          };
        });
    }, [entries, savedData]);

    const sortedRows: ContentRow[] = useMemo(() => {
      if (rows.length === 0) return rows;
      const dir = sortDirection === 'asc' ? 1 : -1;
      const sorted = [...rows];
      sorted.sort((a, b) => {
        switch (sortKey) {
          case 'title':
            return dir * a.title.localeCompare(b.title);
          case 'theme':
            return dir * a.theme.localeCompare(b.theme);
          case 'builder':
            return dir * a.builder.localeCompare(b.builder);
          case 'words':
            return dir * (a.words - b.words);
          case 'lastModified':
          default:
            return dir * (a.lastModifiedTs - b.lastModifiedTs);
        }
      });
      return sorted;
    }, [rows, sortKey, sortDirection]);

    if (rows.length === 0) return null;

    /**
     * Inline sortable header used by each column. Renders the chevron only
     * for the active column (fades in on hover for inactive ones), rotates
     * for desc. Mirrors the pattern in `src/components/common/Table.tsx`
     * without requiring us to switch ArticlesTable's div-based layout to
     * the shared `<table>` component.
     */
    const renderSortableHeader = (
      key: SortKey,
      label: string,
      align: 'left' | 'right' = 'left',
      style?: CSSProperties,
    ) => {
      const isActive = sortKey === key;
      const isDesc = isActive && sortDirection === 'desc';
      return (
        <HeaderCell
          as="button"
          type="button"
          onClick={() => handleSort(key)}
          $sortable
          $active={isActive}
          $align={align}
          aria-sort={isActive ? (isDesc ? 'descending' : 'ascending') : 'none'}
          style={style}
        >
          <span>{label}</span>
          <SortIconWrapper $active={isActive} $desc={isDesc} aria-hidden="true">
            <SortChevronIcon />
          </SortIconWrapper>
        </HeaderCell>
      );
    };

    const handleRowClick = (row: ContentRow) => {
      handleLoadGeneratedContent(
        type === 'blog' ? 'blog' : type === 'email' ? 'email' : 'socialPost',
        videoCreator,
        row.raw,
        setCurrentContent,
      );
      onSelectArticle?.(row.raw.content || '');
    };

    const isBlog = type === 'blog';
    const label =
      type === 'blog'
        ? 'Articles'
        : type === 'email'
          ? 'Emails'
          : 'Social Posts';
    const deleteLabel =
      type === 'blog' ? 'article' : type === 'email' ? 'email' : 'social post';

    return (
      <>
        {!hideSectionTitle && (
          <SectionTitle>
            Saved {label} ({rows.length})
          </SectionTitle>
        )}
        <Table data-testid="articles-table">
          <TableHeader>
            {renderSortableHeader('title', 'Title', 'left', { width: '350px' })}
            <HeaderCell style={{ flex: 1, minWidth: 0 }}>Summary</HeaderCell>
            {isBlog &&
              renderSortableHeader('theme', 'Theme', 'left', {
                width: '140px',
              })}
            {renderSortableHeader('builder', 'Creator', 'left', {
              width: '100px',
            })}
            {renderSortableHeader('words', 'Words', 'left', { width: '70px' })}
            {renderSortableHeader('lastModified', 'Last Modified', 'left', {
              width: '130px',
            })}
            <HeaderCell style={{ width: '48px' }} />
          </TableHeader>
          <TableBody>
            {sortedRows.map(row => (
              <TableRow
                key={row.id}
                onClick={() => handleRowClick(row)}
                data-testid={`article-row-${row.id}`}
              >
                <TableCell style={{ width: '350px' }}>
                  {editingRowId === row.id ? (
                    <TableTitleInput
                      ref={titleInputRef}
                      value={editingValue}
                      onChange={e => setEditingValue(e.target.value)}
                      onBlur={() => handleFinishEdit(row.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          handleFinishEdit(row.id);
                        } else if (e.key === 'Escape') {
                          setEditingRowId(null);
                        }
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                  ) : (
                    <TitleTextWrapper>
                      <TitleText>{row.title}</TitleText>
                      {onRename && (
                        <EditIconButton
                          onClick={e => handleStartEdit(e, row)}
                          title="Rename"
                        >
                          <SimplePencilIcon width="14" height="14" />
                        </EditIconButton>
                      )}
                    </TitleTextWrapper>
                  )}
                </TableCell>
                <TableCell style={{ flex: 1, minWidth: 0 }}>
                  <SummaryText>{row.summary}</SummaryText>
                </TableCell>
                {isBlog && (
                  <TableCell style={{ width: '140px' }}>
                    <SecondaryText>{row.theme}</SecondaryText>
                  </TableCell>
                )}
                <TableCell style={{ width: '100px' }}>
                  <SecondaryText>{row.builder}</SecondaryText>
                </TableCell>
                <TableCell style={{ width: '70px' }}>
                  <SecondaryText>{row.words.toLocaleString()}</SecondaryText>
                </TableCell>
                <TableCell style={{ width: '130px' }}>
                  <SecondaryText>{row.lastModified}</SecondaryText>
                </TableCell>
                <TableCell style={{ width: '48px', justifyContent: 'center' }}>
                  {onDelete && (
                    <DeleteButton
                      onClick={e => {
                        e.stopPropagation();
                        setPendingDeleteId(row.id);
                      }}
                    >
                      <DeleteIcon strokeColor="var(--text-muted)" />
                    </DeleteButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {pendingDeleteId !== null && (
          <Modal isOpen closeModal={() => setPendingDeleteId(null)}>
            <ModalWrapper>
              <ModalHeading accentColor="var(--foreground)">
                Delete this {deleteLabel}?
              </ModalHeading>
              <ModalButtonsContainer>
                <ModalCancelButton
                  accentColor="var(--primary)"
                  onClick={() => setPendingDeleteId(null)}
                >
                  Cancel
                </ModalCancelButton>
                <ModalConfirmButton
                  accentColor="var(--destructive)"
                  onClick={() => {
                    onDelete?.(pendingDeleteId);
                    setPendingDeleteId(null);
                  }}
                >
                  Delete
                </ModalConfirmButton>
              </ModalButtonsContainer>
            </ModalWrapper>
          </Modal>
        )}
      </>
    );
  },
);

export default ArticlesTable;

const SectionTitle = styled.p`
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  margin-top: 24px;
  margin-bottom: 0;
`;

const Table = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--surface);
  border-radius: 8px;
  overflow-x: auto;
  min-width: 0;
  margin-top: 20px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--background);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-muted);
  }
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background-color: var(--background);
  border-bottom: 1px solid var(--border-subtle);
  min-width: 700px;
`;

const HeaderCell = styled.div<{
  $sortable?: boolean;
  $active?: boolean;
  $align?: 'left' | 'right';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'right' ? 'flex-end' : 'flex-start'};
  gap: 4px;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: ${({ $active }) =>
    $active ? 'var(--foreground)' : 'var(--text-muted)'};
  background: transparent;
  border: none;
  padding: 0;
  text-align: ${({ $align }) => $align ?? 'left'};
  ${({ $sortable }) =>
    $sortable &&
    `
      cursor: pointer;
      user-select: none;
      transition: color 0.15s ease;
      &:hover {
        color: var(--foreground);
      }
      &:focus-visible {
        outline: 2px solid var(--primary);
        outline-offset: 2px;
        border-radius: 4px;
      }
    `}
`;

const SortIconWrapper = styled.span<{ $active: boolean; $desc?: boolean }>`
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
  vertical-align: middle;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  transform: ${({ $desc }) => ($desc ? 'rotate(180deg)' : 'rotate(0deg)')};

  /* Fade the chevron in on hover for inactive columns so the affordance
     is discoverable without cluttering idle state. */
  ${HeaderCell}:hover & {
    opacity: 1;
  }
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer;
  transition: background-color var(--transition-fast);
  min-width: 700px;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--surface-elevated);
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
`;

const EditIconButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: var(--text-muted);
  flex-shrink: 0;
  border-radius: 4px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--foreground);
  }
`;

const TitleTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  &:hover ${EditIconButton} {
    display: flex;
  }
`;

const TitleText = styled.span`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--foreground);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const TableTitleInput = styled.input`
  background: var(--surface);
  ${fieldStateCss}
  border-radius: 4px;
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: 500;
  padding: 2px 6px;
  width: 100%;
  outline: none;
  line-height: 1.4;

  &::selection {
    background: var(--primary-tint);
  }
`;

const SummaryText = styled.span`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const SecondaryText = styled.span`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color var(--transition-fast);

  &:hover {
    background-color: rgba(239, 68, 68, 0.2);

    svg path {
      stroke: var(--destructive);
    }
  }
`;
